import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

function DriverService() {
  const { hotelId, roomNumber } = useParams();
  const navigate = useNavigate();
  const [destination, setDestination] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (!destination) return;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.orb1} />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={styles.successPage}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            style={styles.successIconWrap}
          >
            <span style={styles.successIcon}>🚗</span>
          </motion.div>
          <h1 style={styles.successTitle}>Driver Requested</h1>
          <p style={styles.successSub}>We are arranging your driver now</p>

          <div style={styles.successCard}>
            <div style={styles.successRow}>
              <span style={styles.successLabel}>PICKUP</span>
              <span style={styles.successValue}>Hotel Lobby</span>
            </div>
            <div style={styles.successDivider} />
            <div style={styles.successRow}>
              <span style={styles.successLabel}>DESTINATION</span>
              <span style={styles.successValue}>{destination}</span>
            </div>
            <div style={styles.successDivider} />
            <div style={styles.successRow}>
              <span style={styles.successLabel}>ROOM</span>
              <span style={styles.successValue}>{roomNumber}</span>
            </div>
            <div style={styles.successDivider} />
            <div style={styles.successRow}>
              <span style={styles.successLabel}>ETA</span>
              <span style={{ ...styles.successValue, color: '#38BDF8' }}>5-10 minutes</span>
            </div>
          </div>

          <p style={styles.successNote}>
            Our driver will meet you at the hotel lobby. Please be ready.
          </p>

          <motion.button
            whileTap={{ scale: 0.97 }}
            style={styles.backBtn}
            onClick={() => navigate(`/${hotelId}/${roomNumber}`)}
          >
            Back to Services
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.orb1} />
      <div style={styles.orb2} />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={styles.header}
      >
        <motion.button
          whileTap={{ scale: 0.95 }}
          style={styles.backArrow}
          onClick={() => navigate(`/${hotelId}/${roomNumber}`)}
        >
          ← Back
        </motion.button>
        <div style={styles.headerCenter}>
          <span style={styles.headerEmoji}>🚗</span>
          <h1 style={styles.headerTitle}>Driver Service</h1>
          <p style={styles.headerSub}>Safe, reliable rides anytime</p>
        </div>
        <div style={styles.roomBadge}>
          <span style={styles.roomDot} />
          <span style={styles.roomText}>Room {roomNumber}</span>
        </div>
      </motion.div>

      {/* Gold line */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.6 }}
        style={styles.goldLine}
      />

      {/* Notice */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        style={styles.notice}
      >
        <span style={styles.noticeIcon}>🍺</span>
        <p style={styles.noticeText}>
          Had a few drinks? No worries. Our professional drivers will get you home safely and back to the hotel.
        </p>
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        style={styles.form}
      >
        {/* Pickup */}
        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>PICKUP LOCATION</label>
          <div style={styles.pickupBox}>
            <span style={styles.pickupIcon}>📍</span>
            <span style={styles.pickupText}>Hotel Lobby</span>
            <span style={styles.pickupBadge}>Default</span>
          </div>
        </div>

        {/* Destination */}
        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>WHERE TO?</label>
          <div style={styles.inputWrap}>
            <span style={styles.inputIcon}>🗺️</span>
            <input
              style={styles.input}
              placeholder="Enter destination address"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            />
          </div>
        </div>

        {/* Notes */}
        <div style={styles.fieldGroup}>
          <label style={styles.fieldLabel}>SPECIAL NOTES (OPTIONAL)</label>
          <div style={styles.inputWrap}>
            <textarea
              style={styles.textarea}
              placeholder="Any special requests for your driver..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        {/* Info cards */}
        <div style={styles.infoRow}>
          {[
            { icon: '⏱', label: 'ETA', value: '5-10 min' },
            { icon: '👔', label: 'Driver', value: 'Professional' },
            { icon: '🔒', label: 'Safety', value: 'Guaranteed' },
          ].map((info) => (
            <div key={info.label} style={styles.infoCard}>
              <span style={styles.infoIcon}>{info.icon}</span>
              <span style={styles.infoValue}>{info.value}</span>
              <span style={styles.infoLabel}>{info.label}</span>
            </div>
          ))}
        </div>

        {/* Submit */}
        <motion.button
          whileTap={{ scale: 0.97 }}
          style={{
            ...styles.submitBtn,
            opacity: destination ? 1 : 0.4,
            cursor: destination ? 'pointer' : 'not-allowed',
          }}
          onClick={handleSubmit}
        >
          Request Driver →
        </motion.button>
      </motion.div>

      {/* Footer */}
      <div style={styles.footer}>
        <p style={styles.footerText}>POWERED BY <span style={styles.redspeed}>REDSPEED</span></p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#080808',
    maxWidth: '430px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
    position: 'relative',
    overflow: 'hidden',
    paddingBottom: '40px',
  },
  orb1: {
    position: 'absolute', top: '-50px', right: '-100px',
    width: '350px', height: '350px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(56,189,248,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  orb2: {
    position: 'absolute', bottom: '20%', left: '-100px',
    width: '300px', height: '300px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(56,189,248,0.04) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  header: {
    padding: '24px 20px 20px',
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
  },
  backArrow: {
    position: 'absolute', left: '20px', top: '24px',
    background: '#ffffff0a', border: '1px solid #ffffff12',
    borderRadius: '20px', padding: '7px 14px',
    color: '#888', fontSize: '13px', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  headerCenter: { paddingTop: '8px' },
  headerEmoji: { fontSize: '36px', display: 'block', marginBottom: '8px' },
  headerTitle: {
    fontSize: '28px', fontWeight: '700',
    color: '#fff', margin: '0 0 4px',
    letterSpacing: '-0.5px',
  },
  headerSub: { fontSize: '13px', color: '#555', margin: 0 },
  roomBadge: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    background: '#ffffff0a', border: '1px solid #ffffff12',
    borderRadius: '30px', padding: '6px 12px', marginTop: '12px',
  },
  roomDot: {
    width: '6px', height: '6px', borderRadius: '50%',
    background: '#38BDF8', boxShadow: '0 0 6px #38BDF8',
    display: 'inline-block',
  },
  roomText: { fontSize: '12px', fontWeight: '600', color: '#ccc' },
  goldLine: {
    height: '1px',
    background: 'linear-gradient(90deg, transparent, #38BDF844, #38BDF8, #38BDF844, transparent)',
    margin: '4px 24px 20px',
    transformOrigin: 'left',
  },
  notice: {
    margin: '0 16px 20px',
    background: '#38BDF808',
    border: '1px solid #38BDF820',
    borderRadius: '16px',
    padding: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    position: 'relative', zIndex: 1,
  },
  noticeIcon: { fontSize: '24px', flexShrink: 0 },
  noticeText: { fontSize: '13px', color: '#777', lineHeight: 1.5, margin: 0 },
  form: {
    padding: '0 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    position: 'relative', zIndex: 1,
  },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  fieldLabel: {
    fontSize: '10px', letterSpacing: '2px',
    color: '#444', fontWeight: '600',
  },
  pickupBox: {
    background: '#111', border: '1px solid #ffffff10',
    borderRadius: '14px', padding: '14px 16px',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  pickupIcon: { fontSize: '18px' },
  pickupText: { fontSize: '15px', color: '#ccc', flex: 1 },
  pickupBadge: {
    fontSize: '10px', letterSpacing: '1px',
    color: '#38BDF8', background: '#38BDF812',
    border: '1px solid #38BDF830',
    borderRadius: '20px', padding: '3px 10px',
  },
  inputWrap: {
    background: '#111', border: '1px solid #ffffff10',
    borderRadius: '14px', padding: '4px 16px',
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  inputIcon: { fontSize: '18px', flexShrink: 0 },
  input: {
    background: 'transparent', border: 'none', outline: 'none',
    color: '#fff', fontSize: '15px', width: '100%',
    padding: '10px 0', fontFamily: 'inherit',
  },
  textarea: {
    background: 'transparent', border: 'none', outline: 'none',
    color: '#fff', fontSize: '14px', width: '100%',
    padding: '12px 0', fontFamily: 'inherit', resize: 'none',
    lineHeight: 1.5,
  },
  infoRow: {
    display: 'flex', gap: '10px',
  },
  infoCard: {
    flex: 1, background: '#111',
    border: '1px solid #ffffff08',
    borderRadius: '14px', padding: '14px 10px',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '4px',
  },
  infoIcon: { fontSize: '20px' },
  infoValue: { fontSize: '13px', fontWeight: '700', color: '#fff' },
  infoLabel: { fontSize: '10px', color: '#555', letterSpacing: '1px' },
  submitBtn: {
    width: '100%', background: '#38BDF8',
    color: '#000', border: 'none',
    borderRadius: '14px', padding: '16px',
    fontSize: '16px', fontWeight: '700',
    cursor: 'pointer', fontFamily: 'inherit',
    letterSpacing: '0.3px',
  },
  footer: { textAlign: 'center', padding: '32px 0 0' },
  footerText: { fontSize: '9px', letterSpacing: '3px', color: '#333' },
  redspeed: { color: '#FFFFFF' },
  successPage: {
    minHeight: '100vh', display: 'flex',
    flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '40px 24px',
    textAlign: 'center', position: 'relative', zIndex: 1,
  },
  successIconWrap: {
    width: '80px', height: '80px', borderRadius: '24px',
    background: '#38BDF815', border: '1px solid #38BDF830',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', marginBottom: '20px',
  },
  successIcon: { fontSize: '40px' },
  successTitle: {
    fontSize: '30px', fontWeight: '700',
    color: '#fff', margin: '0 0 8px',
  },
  successSub: { fontSize: '14px', color: '#555', marginBottom: '28px' },
  successCard: {
    background: '#111', border: '1px solid #ffffff10',
    borderRadius: '20px', padding: '20px',
    width: '100%', maxWidth: '320px', marginBottom: '24px',
  },
  successRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '8px 0',
  },
  successLabel: { fontSize: '10px', letterSpacing: '2px', color: '#555' },
  successValue: { fontSize: '14px', fontWeight: '600', color: '#fff' },
  successDivider: { height: '1px', background: '#ffffff08' },
  successNote: {
    fontSize: '13px', color: '#555',
    lineHeight: 1.6, marginBottom: '28px',
    maxWidth: '280px',
  },
  backBtn: {
    background: '#ffffff0a', border: '1px solid #ffffff15',
    borderRadius: '14px', padding: '14px 32px',
    color: '#fff', fontSize: '15px',
    fontWeight: '600', cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default DriverService;